import { createSelector } from '@reduxjs/toolkit';

import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import { conditionIsUncertain } from '@/shared/embeddedLibs/conditionsAndMasks';

import { RootState } from '@/app/model/store';

import {
  conditionIsActive,
  conditionIsActiveForFDA,
  filterConditionsByReportID,
  getSlicesMasks,
  processLogicalConditions,
} from '../lib';

import { conditionAdapter } from './conditionSlice';

export const conditionSelectors = conditionAdapter.getSelectors(
  (state: RootState) => state.condition,
);

export const { selectAll, selectEntities } = conditionSelectors;

export const selectByID = (toothID: string) => (state: RootState) =>
  conditionSelectors.selectById(state, toothID);

export const selectToothConditionIDs =
  (toothID: string) => (state: RootState) =>
    state.condition.teethGroup[toothID];

export const selectTeethConditionIDs =
  (teethIDs: string[]) => (state: RootState) =>
    teethIDs.flatMap((toothID) => state.condition.teethGroup[toothID]);

export const selectTeethGroup = (state: RootState) =>
  state.condition.teethGroup;

export const selectHoveredConditionIDs = (state: RootState) =>
  state.condition.hoveredConditionIDs;

export const selectMaxFaxConditionIDs = (state: RootState) =>
  state.condition.maxFaxGroup;

export const selectGroupedByToothIDConditions = createSelector(
  selectAll,
  (conditions) =>
    conditions.reduce(
      (groupedConditions, condition) => {
        const toothID =
          condition.Attribution.case === 'Tooth'
            ? condition.Attribution.value.ToothID
            : '';

        if (toothID in groupedConditions) {
          groupedConditions[toothID].push(condition);
        } else {
          groupedConditions[toothID] = [condition];
        }

        return groupedConditions;
      },
      {} as Record<string, Condition[]>,
    ),
);

export const selectByToothID = (toothID: string) =>
  createSelector(
    conditionSelectors.selectEntities,
    selectToothConditionIDs(toothID),
    (conditionEntities, toothConditionIDs) => {
      const toothConditions: Condition[] = [];

      toothConditionIDs?.forEach((id) => {
        if (conditionEntities[id]) {
          toothConditions.push(conditionEntities[id]);
        }
      });
      return toothConditions;
    },
  );

export const selectActiveByToothID = (toothID: string) =>
  createSelector(
    conditionSelectors.selectEntities,
    selectToothConditionIDs(toothID),
    (conditionEntities, toothConditionIDs) => {
      const toothConditions: Condition[] = [];

      toothConditionIDs?.forEach((id) => {
        if (conditionEntities[id] && conditionIsActive(conditionEntities[id])) {
          toothConditions.push(conditionEntities[id]);
        }
      });
      return toothConditions;
    },
  );

export const selectPreviewActiveByToothID = (toothID: string) =>
  createSelector(
    conditionSelectors.selectEntities,
    selectToothConditionIDs(toothID),
    (conditionEntities, toothConditionIDs) => {
      const toothConditions: Condition[] = [];

      toothConditionIDs?.forEach((id) => {
        if (
          conditionEntities[id] &&
          conditionIsActive(conditionEntities[id]) &&
          !conditionIsUncertain(conditionEntities[id].Certainty)
        ) {
          toothConditions.push(conditionEntities[id]);
        }
      });
      return toothConditions;
    },
  );

export const selectHoveredConditions = createSelector(
  conditionSelectors.selectEntities,
  selectHoveredConditionIDs,
  (conditionEntities, hoveredConditionIDs) => {
    const hoveredConditions: Condition[] = [];

    hoveredConditionIDs?.forEach((id) => {
      if (conditionEntities[id] && conditionIsActive(conditionEntities[id])) {
        hoveredConditions.push(conditionEntities[id]);
      }
    });
    return hoveredConditions;
  },
);

// Collect all yellow (uncertain) condition ids to use it with approve tooth request
export const selectYellowIDsByToothID = (toothID: string) =>
  createSelector(
    conditionSelectors.selectEntities,
    selectToothConditionIDs(toothID),
    (conditionEntities, toothConditionIDs) => {
      const yellowConditionIDs: string[] = [];

      toothConditionIDs?.forEach((id) => {
        if (
          conditionEntities[id] &&
          conditionIsUncertain(conditionEntities[id].Certainty)
        ) {
          yellowConditionIDs.push(conditionEntities[id].ID);
        }
      });
      return yellowConditionIDs;
    },
  );

export const selectToothLogicalConditions = (toothID: string) =>
  createSelector(selectByToothID(toothID), processLogicalConditions);

export const selectToothActiveLogicalConditions = (toothID: string) =>
  createSelector(selectActiveByToothID(toothID), processLogicalConditions);

export const selectToothPreviewActiveLogicalConditions = (toothID: string) =>
  createSelector(
    selectPreviewActiveByToothID(toothID),
    processLogicalConditions,
  );

// Include parent PBL conditions when their child conditions are active,
// even if the parent itself doesn't have EngineDecision === Positive.
// This prevents children from being orphaned in processLogicalConditions.
const includeParentPBLWithActiveChildren = (
  allConditions: Condition[],
  activeConditions: Condition[],
): Condition[] => {
  const activeIds = new Set(activeConditions.map((c) => c.ID));

  const activeChildPBLParentIDs = new Set(
    activeConditions
      .filter((c) => c.ParentCode === ConditionCode.PeriodontalBoneLoss)
      .map((c) => c.ParentID)
      .filter(Boolean),
  );

  if (activeChildPBLParentIDs.size === 0) {
    return activeConditions;
  }

  const result = activeConditions.slice();

  allConditions.forEach((condition) => {
    if (
      condition.Code === ConditionCode.PeriodontalBoneLoss &&
      !activeIds.has(condition.ID) &&
      condition.ID &&
      activeChildPBLParentIDs.has(condition.ID)
    ) {
      result.push(condition);
    }
  });

  return result;
};

export const selectPreviewActiveByToothIDForCBCTGP = (toothID: string) =>
  createSelector(
    selectByToothID(toothID),
    selectPreviewActiveByToothID(toothID),
    (allConditions, activeConditions) =>
      includeParentPBLWithActiveChildren(allConditions, activeConditions),
  );

export const selectToothPreviewActiveLogicalConditionsForCBCTGP = (
  toothID: string,
) =>
  createSelector(
    selectPreviewActiveByToothIDForCBCTGP(toothID),
    processLogicalConditions,
  );

export const selectActiveByToothIDForCBCTGP = (toothID: string) =>
  createSelector(
    selectByToothID(toothID),
    selectActiveByToothID(toothID),
    (allConditions, activeConditions) =>
      includeParentPBLWithActiveChildren(allConditions, activeConditions),
  );

export const selectToothActiveLogicalConditionsForCBCTGP = (toothID: string) =>
  createSelector(
    selectActiveByToothIDForCBCTGP(toothID),
    processLogicalConditions,
  );

// FDA 2.0 selectors: include conditions with UserDecision/UserScore (manual annotations)
export const selectActiveByToothIDForFDAVersion2 = (toothID: string) =>
  createSelector(
    conditionSelectors.selectEntities,
    selectToothConditionIDs(toothID),
    (conditionEntities, toothConditionIDs) => {
      const toothConditions: Condition[] = [];

      toothConditionIDs?.forEach((id) => {
        if (
          conditionEntities[id] &&
          conditionIsActiveForFDA(conditionEntities[id])
        ) {
          toothConditions.push(conditionEntities[id]);
        }
      });
      return toothConditions;
    },
  );

export const selectToothActiveLogicalConditionsForFDAVersion2 = (
  toothID: string,
) =>
  createSelector(
    selectActiveByToothIDForFDAVersion2(toothID),
    processLogicalConditions,
  );

export const selectActiveByToothIDForCBCTGPAndFDAVersion2 = (toothID: string) =>
  createSelector(
    selectByToothID(toothID),
    selectActiveByToothIDForFDAVersion2(toothID),
    (allConditions, activeConditions) =>
      includeParentPBLWithActiveChildren(allConditions, activeConditions),
  );

export const selectToothActiveLogicalConditionsForCBCTGPAndFDAVersion2 = (
  toothID: string,
) =>
  createSelector(
    selectActiveByToothIDForCBCTGPAndFDAVersion2(toothID),
    processLogicalConditions,
  );

export const selectByTeethIDs = (teethID: string[]) =>
  createSelector(
    conditionSelectors.selectEntities,
    selectTeethConditionIDs(teethID),
    (conditionEntities, teeththConditionIDs) => {
      const toothConditions: Condition[] = [];

      teeththConditionIDs?.forEach((id) => {
        if (conditionEntities[id]) {
          toothConditions.push(conditionEntities[id]);
        }
      });
      return toothConditions;
    },
  );

export const selectByReportID = createSelector(
  conditionSelectors.selectAll,
  (_: RootState, reportID: string) => reportID,
  filterConditionsByReportID,
);

export const selectMaxFax = createSelector(
  conditionSelectors.selectEntities,
  selectMaxFaxConditionIDs,
  (conditionEntities, maxFaxIDs) => {
    const maxFaxConditions: Condition[] = [];

    maxFaxIDs?.forEach((id) => {
      if (conditionEntities[id] && conditionIsActive(conditionEntities[id])) {
        maxFaxConditions.push(conditionEntities[id]);
      }
    });
    return maxFaxConditions;
  },
);

export const selectMaxFaxGroupedByCode = createSelector(
  selectMaxFax,
  (maxFaxConditions) => {
    return maxFaxConditions.reduce(
      (acc, condition) => {
        const code = condition.Code;
        if (!acc[code]) {
          acc[code] = [];
        }
        acc[code].push(condition);
        return acc;
      },
      {} as Record<ConditionCode, Condition[]>,
    );
  },
);

export const selectSlicesMasks = (
  toothID: string,
  conditionCodes: ConditionCode[],
) => createSelector(selectByToothID(toothID), getSlicesMasks(conditionCodes));

export const selectPeriapicalConditionByToothID = (toothID: string) =>
  createSelector(selectByToothID(toothID), (conditions) =>
    conditions.find(
      (condition) => condition.Code === ConditionCode.PeriapicalRadiolucency,
    ),
  );

export const selectLoading = (state: RootState) => state.condition.loading;

export const selectReportConditionsSnapshot = (state: RootState) =>
  state.condition.reportConditionsSnapshot;

export const selectHoveredNonDentalConditionIDs = (state: RootState) =>
  state.condition.hoveredNonDentalConditionIDs;
